"use client";

import * as React from "react";
import { AlertDialog, Button, Description, Modal, Spinner, toast } from "@heroui/react";
import { copyText } from "@hs/shared";
import { trpc } from "@/lib/trpc";
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

const HOME_ORIGIN =
  (typeof import.meta !== "undefined" &&
    (import.meta as { env?: { VITE_HOME_ORIGIN?: string } }).env?.VITE_HOME_ORIGIN) ||
  "";

export interface InviteRegisterDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  person: { id: string; fullName?: string | null; email?: string | null } | null;
  spaceId: string | undefined;
  onSuccess?: () => void;
}

export function InviteRegisterDialog({
  open,
  onOpenChange,
  person,
  spaceId,
  onSuccess,
}: InviteRegisterDialogProps) {
  const [inviteUrl, setInviteUrl] = React.useState<string | null>(null);
  const [displayText, setDisplayText] = React.useState<string>("");
  const [isNoEmailDialogOpen, setIsNoEmailDialogOpen] = React.useState(false);

  const getRoleByCode = trpc.spaceRoles.getByCode.useQuery(
    { spaceId: spaceId!, code: "MEMBER" },
    { enabled: open && !!spaceId }
  );

  const createInvitation = trpc.invitationCodes.create.useMutation({
      onSuccess: (data) => {
      const code = (data as { code?: string })?.code;
      if (code) {
        const base = HOME_ORIGIN.replace(/\/$/, "");
        const url = `${base}/inviter?code=${code}`;
        setInviteUrl(url);
        const fullName = person?.fullName || "您";
        setDisplayText(
          `亲爱的${fullName}，请点击以下网址注册"人文派"，${url}，注册成功后退出页面，然后搜索关注"人文派"服务号，点击菜单登录，进入【我的】，设置头像及昵称，即可正常使用海报及活动各项功能。`
        );
      }
    },
    onError: () => {
      toast.danger("生成邀请链接失败");
    },
  });

  const hasCreatedRef = React.useRef(false);

  React.useEffect(() => {
    if (!open || !person?.id || !spaceId || inviteUrl != null || hasCreatedRef.current) return;
    if (getRoleByCode.isSuccess && getRoleByCode.data) {
      hasCreatedRef.current = true;
      const role = getRoleByCode.data as { id: string };
      createInvitation.mutate({
        name: "邀请空间成员",
        type: "JOIN",
        spaceId,
        maxUses: 1,
        status: "ACTIVE",
        config: { role_id: role.id, people_id: person.id },
      });
    }
  }, [open, person?.id, person?.fullName, spaceId, getRoleByCode.isSuccess, getRoleByCode.data, inviteUrl]);

  React.useEffect(() => {
    if (!open) hasCreatedRef.current = false;
  }, [open]);

  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      if (!next) {
        setInviteUrl(null);
        setDisplayText("");
      }
      onOpenChange(next);
    },
    [onOpenChange]
  );

  const sendMailMutation = trpc.mailer.send.useMutation({
    onSuccess: () => {
      toast.success("邮件发送成功");
    },
    onError: (error) => {
      toast.danger(error.message || "邮件发送失败");
    },
  });

  const handleCopy = React.useCallback(async () => {
    if (!displayText) return;
    const ok = await copyText(displayText);
    if (ok) {
      toast.success("已复制到剪贴板");
    } else {
      toast.danger("复制失败，请手动选中文案后 Ctrl+C（Mac：Cmd+C）复制");
    }
  }, [displayText]);

  const handleSendEmail = React.useCallback(() => {
    if (!displayText) return;
    const email = person?.email?.trim();
    if (!email) {
      setIsNoEmailDialogOpen(true);
      return;
    }
    sendMailMutation.mutate({
      to: email,
      subject: "人文派邀请注册",
      contentType: "text",
      content: displayText,
    });
  }, [displayText, person?.email, sendMailMutation]);

  const isLoading =
    getRoleByCode.isLoading ||
    createInvitation.isPending ||
    (open && !!person && !!spaceId && !inviteUrl && !getRoleByCode.error);

  return (
    <>
    <Modal isOpen={open} onOpenChange={handleOpenChange}>
      <RacDialogTriggerAnchor />
      <Modal.Backdrop>
        <Modal.Container placement="center" scroll="inside" size="md">
          <Modal.Dialog className="flex max-h-[90vh] max-w-md flex-col">
            <Modal.Header className="gap-1">
              <Modal.Heading className="text-xl font-medium">邀请注册</Modal.Heading>
              <Description className="sr-only">生成邀请注册链接并复制文案到剪贴板</Description>
            </Modal.Header>
            <Modal.Body className="flex min-h-0 flex-1 flex-col gap-4 overflow-auto p-4">
              {open && person && !spaceId ? (
                <p className="text-muted-foreground text-sm">无法获取当前空间，无法生成邀请链接。</p>
              ) : null}
              {(getRoleByCode.error || createInvitation.error) && (
                <p className="text-danger text-sm">
                  {getRoleByCode.error ? "获取空间角色失败，请稍后重试。" : "生成邀请链接失败，请稍后重试。"}
                </p>
              )}
              {isLoading ? (
                <div className="flex flex-1 items-center justify-center py-8">
                  <Spinner className="size-8" />
                </div>
              ) : null}
              {!isLoading && displayText ? (
                <p className="text-muted-foreground select-text break-all text-sm whitespace-pre-wrap">
                  {displayText}
                </p>
              ) : null}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" isDisabled={!displayText} onPress={handleCopy}>
                复制文案
              </Button>
              <Button
                isDisabled={!displayText || sendMailMutation.isPending}
                onPress={handleSendEmail}
              >
                {sendMailMutation.isPending ? "发送中…" : "邮件通知"}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>

    <AlertDialog isOpen={isNoEmailDialogOpen} onOpenChange={setIsNoEmailDialogOpen}>
      <RacDialogTriggerAnchor />
      <AlertDialog.Backdrop>
        <AlertDialog.Container placement="center">
          <AlertDialog.Dialog>
            <AlertDialog.Header>
              <AlertDialog.Heading>邮件发送失败</AlertDialog.Heading>
            </AlertDialog.Header>
            <AlertDialog.Body>该员工未配置邮箱，请进行配置。</AlertDialog.Body>
            <AlertDialog.Footer>
              <Button variant="primary" onPress={() => setIsNoEmailDialogOpen(false)}>
                知道了
              </Button>
            </AlertDialog.Footer>
          </AlertDialog.Dialog>
        </AlertDialog.Container>
      </AlertDialog.Backdrop>
    </AlertDialog>
    </>
  );
}
